import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
} from "@mui/material"
import { ExpandMore, FormatQuote } from "@mui/icons-material"

const getCompanyColorByName = (companyName) => {
  const colors = ["#407775", "#2E5266", "#1A3A47", "#6B8E23", "#8B4513", "#4B0082"]
  const index = companyName.charCodeAt(0) % colors.length
  return colors[index] || "#407775"
}

const QuotesComparison = ({ quotesComparisonData = {} }) => {
  // Use API data if available, otherwise fall back to empty array
  const themes = quotesComparisonData.themes ? Object.entries(quotesComparisonData.themes) : []

  return (
    <Box sx={{ p: 3, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: "#407775",
            fontWeight: "bold",
            mb: 1,
          }}
        >
          Thematic Quote Comparison
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#707070",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Compare key statements and commitments across companies by thematic areas
        </Typography>
      </Box>

      {themes.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
          }}
        >
          <FormatQuote sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{
              color: "#707070",
              fontStyle: "italic",
            }}
          >
            No thematic quote data available
          </Typography>
        </Paper>
      )}

      {themes.map(([themeKey, themeData], index) => (
        <Accordion
          key={themeKey}
          defaultExpanded={index === 0}
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            border: "1px solid #e0e0e0",
            boxShadow: "none",
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: "0 0 16px 0",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: "#407775" }} />}
            sx={{
              backgroundColor: "#f8fafa",
              borderRadius: "12px",
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormatQuote sx={{ color: "#407775" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#407775",
                }}
              >
                {themeKey}
              </Typography>
              <Chip
                label={`${Array.isArray(themeData) ? themeData.length : 0} Companies`}
                size="small"
                sx={{
                  backgroundColor: "#407775",
                  color: "white",
                  fontWeight: "medium",
                }}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: "200px",
                        color: "#407775",
                        borderBottom: "2px solid #407775",
                      }}
                    >
                      Company
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#407775",
                        borderBottom: "2px solid #407775",
                      }}
                    >
                      Statement
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(themeData) ? (
                    themeData.map((item, idx) => (
                      <TableRow
                        key={`${themeKey}-${idx}`}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "#fafafa",
                          },
                          "&:hover": {
                            backgroundColor: "#f0f7f7",
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: getCompanyColorByName(item.company_name),
                                fontSize: "0.875rem",
                                fontWeight: "bold",
                              }}
                            >
                              {item.company_name.charAt(0)}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "medium",
                                color: "#707070",
                              }}
                            >
                              {item.company_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ position: "relative", pl: 2 }}>
                            <FormatQuote
                              sx={{
                                position: "absolute",
                                left: -8,
                                top: -4,
                                fontSize: 16,
                                color: "#407775",
                                opacity: 0.6,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "italic",
                                color: "#707070",
                                lineHeight: 1.6,
                              }}
                            >
                              {item.quote}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#999",
                            fontStyle: "italic",
                          }}
                        >
                          No data available for this theme
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default QuotesComparison
